import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Paper, Grid } from "@mui/material";
import { Helmet } from "react-helmet-async";
import DownloadPDFButton from "../components/ReportPDF"; // Import the new PDF component

import MailServerReport from "../components/Reports/Mailserver";
import CookieScanReport from "../components/Reports/CookieScanner";
import PentestReport from "../components/Reports/PentestReport";
import AttackSurfaceReport from "../components/Reports/AttackSurface";
import PrivacyPolicyReport from "../components/Reports/PrivacyPolicy";
import BlacklistResults from "../components/Reports/BlackListReport";
import SSLReport from "../components/Reports/SSLReport";
import DNSReport from "../components/Reports/DNSReport";

function FreeToolsDetails() {
  const { type, id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const map = {
    dnscheck: "DNS Report",
    sslcheck: "SSL Certificate Report",
    mailservercheck: "Mail Server Report",
    cookiescanner: "Cookie Report",
    pentester: "Pen Testing Results",
    attacksurfacemapper: "Attack Surface Report",
    privacypolicycheck: "Privacy Policy Document Report",
    gdprcheck: "GDPR Document Report",
    toscheck: "Terms of Service Document Report",
    blacklistcheck: "Blacklist Report",
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `https://rq5j44ymda.execute-api.us-east-1.amazonaws.com/dev/FreeToolsData?type=${type}&id=${id}`
        );
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Failed to fetch data");

        setData(result.results[0]);
      } catch (err) {
        setData(null);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type, id]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" p={3} bgcolor="#f9fafb" minHeight="100vh">
      <Helmet>
        <title>{`${map[type]} - Portend AI`}</title>
        <meta name="description" content={`View details of ${type} report with ID ${id}.`} />
      </Helmet>

      {loading ? (
        <CircularProgress color="primary" />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {/* ✅ Header Section with PDF Download Button */}
          <Paper
            sx={{
              width: "100%",
              maxWidth: "915px",
              padding: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>
              Want to run such checks regularly on a schedule? Sign up{" "}
              <a href="https://portend.ai" target="_blank">
                here
              </a>
            </Typography>
         {(Object.keys(data).indexOf('error') === -1)&&(<DownloadPDFButton data={data} title={map[type]} type={type} pageTitle={map[type]}/>)}
          </Paper>

          {/* ✅ Report Section */}
          {data ? (
            <Box sx={{ width: "100%", mt: 2 }}>
              {type === "dnscheck" && <DNSReport data={data} pageTitle={map[type]} />}
              {type === "sslcheck" && <SSLReport data={data} pageTitle={map[type]} />}
              {type === "mailservercheck" && <MailServerReport data={data} pageTitle={map[type]} />}
              {type === "cookiescanner" && <CookieScanReport data={data} />}
              {type === "pentester" && <PentestReport data={data} pageTitle={map[type]} />}
              {type === "attacksurfacemapper" && <AttackSurfaceReport data={data} pageTitle={map[type]} />}
              {(type === "privacypolicycheck" || type === "gdprcheck" || type === "toscheck") && (
                <PrivacyPolicyReport data={data} pageTitle={map[type]} />
              )}
              {type === "blacklistcheck" && <BlacklistResults data={data} />}
            </Box>
          ) : (
            <Paper
              elevation={3}
              sx={{ padding: 3, width: "100%", maxWidth: 900, margin: "0 auto", mt: 2 }}
            >
              <Typography variant="h4" fontWeight="bold" mb={3} color="rgb(0, 145, 70)" sx={{ textAlign: "center" }}>
                {map[type]}
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}

export default FreeToolsDetails;
